import * as Sentry from '@sentry/browser'
import { ReactNode, useEffect } from 'react'
import { FallbackProps } from 'react-error-boundary'
import { useRouteError } from 'react-router-dom'

import { link } from '../services/routing'
import { Button } from './button'
import { Heading, Paragraph } from './typography'

interface ErrorViewProps {
  /**
   * Title of the message
   */
  title?: ReactNode

  /**
   * Body text
   */
  message?: ReactNode

  /**
   * Show contact support text under the message
   *
   * @default true
   */
  showContact?: boolean

  /**
   * Show refresh page button under the text
   *
   * @default true
   */
  showRefresh?: boolean
}

export function ErrorView({ title, message, showContact = true, showRefresh = true }: ErrorViewProps) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <Heading level={2}>{title}</Heading>
      <Paragraph className="mt-2 w-96 text-center">{message}</Paragraph>

      {showContact && (
        <Paragraph className="mt-4 w-96 text-center">
          Our development team has been notified of this. If the error persists, please{' '}
          <a href={link.toContact()} className="text-blue-600 underline hover:no-underline">
            contact support
          </a>
          .
        </Paragraph>
      )}

      {showRefresh && (
        <Button as="a" className="mt-6" href={window.location.href}>
          Refresh page
        </Button>
      )}
    </div>
  )
}

export function DefaultErrorBoundary(props: Partial<FallbackProps>) {
  useEffect(() => {
    Sentry.captureException(props.error)
  }, [props.error])

  return <ErrorView title="Application error" message="We’re sorry but an unknown error occured." />
}

export function RouterErrorElement() {
  const error = useRouteError() as Error
  return <DefaultErrorBoundary error={error} />
}
